/**
 * Utility functions for chat widget chatroom management
 * Handles chatroom creation and search token generation
 */

import { doc, setDoc, getDoc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore'
import type { Firestore } from 'firebase/firestore'
import { generateVisitorId, generateDisplayNumberFromId } from '../api-key-validator'

const VISITOR_ID_KEY = 'chat_widget_visitor_id'
const VISITOR_NAME_KEY = 'chat_widget_visitor_name'

/**
 * Build search array for server-side searching in Firestore
 * Focus on meaningful tokens only:
 * - visitorId: "cwv_1f4daf96dd4b4f61" → full ID, hash part, last 4 chars
 * - visitorName: "Visitor #3039" → full name, the number (3039)
 */
export function buildSearchArray(visitorId: string, visitorName: string): string[] {
    const searchTokens = new Set<string>()

    if (visitorId) {
        const lowerVisitorId = visitorId.toLowerCase()
        // Full visitor ID
        searchTokens.add(lowerVisitorId)

        // Extract hash part after "cwv_" (e.g., "1f4daf96dd4b4f61")
        if (lowerVisitorId.startsWith('cwv_')) {
            const hashPart = lowerVisitorId.substring(4)
            if (hashPart.length > 0) {
                searchTokens.add(hashPart)
            }
        }

        // Last 4 characters for quick lookup
        if (lowerVisitorId.length >= 4) {
            searchTokens.add(lowerVisitorId.slice(-4))
        }
    }

    if (visitorName) {
        const lowerName = visitorName.toLowerCase()
        // Full visitor name
        searchTokens.add(lowerName)

        // Extract number from "Visitor #3039" format
        const numberMatch = visitorName.match(/#(\d+)/)
        if (numberMatch && numberMatch[1]) {
            const visitorNumber = numberMatch[1]
            // Full number (e.g., "3039")
            searchTokens.add(visitorNumber)
            // Last 4 digits if number is longer
            if (visitorNumber.length > 4) {
                searchTokens.add(visitorNumber.slice(-4))
            }
        }
    }

    return Array.from(searchTokens)
}

interface EnsureChatRoomParams {
    firestore: Firestore
    chatRoomId: string
    clientId: string
    visitorId: string
    visitorName: string
    apiKey: string
}

/**
 * Ensure chatroom exists - create if it doesn't exist yet
 * This implements the "lazy creation" pattern - chatroom is created on first message/interaction
 * 
 * @returns true if chatroom was created, false if it already existed
 */
export async function ensureChatRoomExists({
    firestore,
    chatRoomId,
    clientId,
    visitorId,
    visitorName,
    apiKey,
}: EnsureChatRoomParams): Promise<boolean> {
    const chatRoomRef = doc(firestore, 'widgetChatRooms', chatRoomId)
    
    try {
        // Check if chatroom already exists
        const chatRoomDoc = await getDoc(chatRoomRef)
        
        if (chatRoomDoc.exists()) {
            // Chatroom already exists, no need to create
            return false
        }

        // Chatroom doesn't exist, create it with full initialization
        const now = Timestamp.now()
        
        await setDoc(chatRoomRef, {
            id: chatRoomId,
            chatRoomId,
            clientId,
            apiKey, // Store API key for inbox filtering
            from: visitorId,
            to: clientId,
            name: visitorName,
            remoteJid: visitorId,
            object: 'chat_widget',
            date: now,
            aiStatus: 'completed',
            search: buildSearchArray(visitorId, visitorName), // Add search tokens for server-side search
        })
        
        console.log('[Chat Widget] Created new chatroom on first interaction:', chatRoomId)
        return true
    } catch (error) {
        // If error is because chatroom was created concurrently by another call, that's fine
        // Otherwise, log the error but don't throw - we'll try to send the message anyway
        console.error('[Chat Widget] Error ensuring chatroom exists:', error)
        return false
    }
}

/**
 * Get the active chatRoomId for a returning visitor.
 * Reads currentChatRoomId field from the base chatroom doc.
 * If not set, returns baseChatRoomId (session 1 behaviour).
 */
export async function getActiveChatRoomId({
    firestore,
    baseChatRoomId,
}: {
    firestore: Firestore
    baseChatRoomId: string
    clientId: string
    visitorId: string
    apiKey: string
}): Promise<string> {
    try {
        const baseRef = doc(firestore, 'widgetChatRooms', baseChatRoomId)
        const baseSnap = await getDoc(baseRef)
        if (baseSnap.exists()) {
            const data = baseSnap.data()
            return data.currentChatRoomId || baseChatRoomId
        }
    } catch (error) {
        console.warn('[Chat Widget] Could not read base chatroom for session:', error)
    }
    return baseChatRoomId
}

/**
 * Check if the active chatroom has exceeded inactivityResetDuration hours.
 * If expired: archives the old chatroom, cleans up flow state, generates a new
 * visitorId + chatRoomId, updates localStorage and widgetVisitorSessions.
 * Returns the chatRoomId to use (same if not expired, new if reset).
 */
export async function checkAndResetIfExpired({
    firestore,
    chatRoomId,
    clientId,
    visitorId,
    apiKey,
    inactivityResetDuration,
}: {
    firestore: Firestore
    chatRoomId: string
    clientId: string
    visitorId: string
    apiKey: string
    inactivityResetDuration?: number | null
}): Promise<{ chatRoomId: string; visitorId: string; visitorName: string }> {
    // If feature disabled, return as-is
    if (!inactivityResetDuration || inactivityResetDuration <= 0) {
        const currentName = (typeof window !== 'undefined' && window.localStorage)
            ? localStorage.getItem('chat_widget_visitor_name') || ''
            : ''
        return { chatRoomId, visitorId, visitorName: currentName }
    }

    try {
        const chatRoomRef = doc(firestore, 'widgetChatRooms', chatRoomId)
        const chatRoomSnap = await getDoc(chatRoomRef)

        const currentName = (typeof window !== 'undefined' && window.localStorage)
            ? localStorage.getItem(VISITOR_NAME_KEY) || ''
            : ''

        // No chatroom yet (first visit) — nothing to reset
        if (!chatRoomSnap.exists()) {
            return { chatRoomId, visitorId, visitorName: currentName }
        }

        const data = chatRoomSnap.data()
        const lastDate: Date = data.date?.toDate?.() ?? new Date(data.date)
        const hoursSinceLastMessage = (Date.now() - lastDate.getTime()) / (1000 * 60 * 60)

        // Not expired yet
        if (hoursSinceLastMessage < inactivityResetDuration) {
            return { chatRoomId, visitorId, visitorName: currentName }
        }

        // --- Expired: perform reset ---
        console.log(`[Chat Widget] Session expired (${Math.round(hoursSinceLastMessage)}h idle). Resetting.`)

        // 1. Archive old chatroom
        await updateDoc(chatRoomRef, { status: 'expired' })

        // 2. Clean up flow assigned numbers (fire and forget — same pattern as chatroom deletion fix)
        deleteDoc(doc(firestore, 'whatsappFlowAssignedNumbers', chatRoomId)).catch(() => {})

        // 3. Generate new visitorId, name and chatRoomId
        const newVisitorId = generateVisitorId()
        const newVisitorName = `Visitor #${generateDisplayNumberFromId(newVisitorId)}`
        const newChatRoomId = `${clientId}-${newVisitorId}-${apiKey}`

        // 4. Update localStorage — fresh identity, clear old session's lastSeen
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                localStorage.setItem(VISITOR_ID_KEY, newVisitorId)
                localStorage.setItem(VISITOR_NAME_KEY, newVisitorName)
                localStorage.removeItem(`chat_widget_${visitorId}_lastSeen`)
            } catch {
                console.warn('[Chat Widget] Could not update localStorage after session reset')
            }
        }

        // 5. Update base chatroom doc with session tracking fields
        const baseChatRoomId = `${clientId}-${visitorId}-${apiKey}`
        const baseRef = doc(firestore, 'widgetChatRooms', baseChatRoomId)
        const baseSnap = await getDoc(baseRef)

        if (baseSnap.exists()) {
            await updateDoc(baseRef, { currentChatRoomId: newChatRoomId })
        } else {
            // Base doc doesn't exist (edge case) — create it as anchor
            await setDoc(baseRef, {
                id: baseChatRoomId,
                chatRoomId: baseChatRoomId,
                clientId,
                apiKey,
                object: 'chat_widget',
                currentChatRoomId: newChatRoomId,
            })
        }

        console.log('[Chat Widget] Session reset. New chatRoomId:', newChatRoomId)
        return { chatRoomId: newChatRoomId, visitorId: newVisitorId, visitorName: newVisitorName }

    } catch (error) {
        console.error('[Chat Widget] Error during session reset check:', error)
        const fallbackName = (typeof window !== 'undefined' && window.localStorage)
            ? localStorage.getItem(VISITOR_NAME_KEY) || ''
            : ''
        return { chatRoomId, visitorId, visitorName: fallbackName }
    }
}

